import {clamp} from './helpers';
import {$_PARAMS} from './filters/all';

export interface ParamJson<T> {
  kind: string;
  value: T;
}

/**
 * A tunable parameter for a filter.
 */
export abstract class Parameter {
  static $_kind: string;
  $_value: any;

  constructor() {}

  abstract serialize(): any;
}

export interface RangeParameterJSON extends ParamJson<number> {
  initial: number;
  min: number;
  max: number;
}

export class RangeParameter extends Parameter {
  static $_kind = 'range_parameter';
  public $_value: number;

  constructor(
    readonly $_initial: number,
    readonly $_min: number,
    readonly $_max: number
  ) {
    super();
    this.$_value = this.clamp($_initial);
  }

  clamp(value: number) {
    return clamp(value, this.$_min, this.$_max);
  }

  set(newValue: number) {
    this.$_value = this.clamp(newValue);
  }

  serialize(): RangeParameterJSON {
    return {
      kind: RangeParameter.$_kind,
      initial: this.$_initial,
      min: this.$_min,
      max: this.$_max,
      value: this.$_value,
    };
  }

  static deserialize(json: RangeParameterJSON): RangeParameter {
    const param = new RangeParameter(json.initial, json.min, json.max);
    param.set(json.value);
    return param;
  }
}

export interface NullableRangeParameterJSON extends ParamJson<number> {
  title: string;
  initial: number;
  min: number;
  max: number;
  value: number;
  active: boolean;
  integerStep: boolean;
}

const DECIMAL_STEP = 0.01;

export class NullableRangeParameter extends Parameter {
  static $_kind = 'nullable_range_parameter';
  public $_value: number | null;
  public $_numericValue: number;

  constructor(
    readonly $_title: string,
    public $_active: boolean,
    readonly $_initial: number,
    readonly $_min: number,
    readonly $_max: number,
    readonly $_integerStep: boolean = true
  ) {
    super();
    const value = this.clamp($_initial);
    this.$_value = this.$_active ? value : null;
    this.$_numericValue = value;
  }

  get step(): number {
    if (this.$_integerStep) return 1;
    return DECIMAL_STEP;
  }

  clamp(value: number) {
    return clamp(value, this.$_min, this.$_max);
  }

  setActive(newActive: boolean) {
    this.$_active = newActive;
    if (!this.$_active) {
      this.$_value = null;
    } else {
      this.$_value = this.$_numericValue;
    }
  }

  set(newValue: number) {
    const value = this.clamp(newValue);
    this.$_value = this.$_active ? value : null;
    this.$_numericValue = value;
  }

  serialize(): NullableRangeParameterJSON {
    return {
      kind: NullableRangeParameter.$_kind,
      title: this.$_title,
      initial: this.$_initial,
      min: this.$_min,
      max: this.$_max,
      value: this.$_numericValue,
      active: this.$_active,
      integerStep: this.$_integerStep,
    };
  }

  static deserialize(json: NullableRangeParameterJSON): NullableRangeParameter {
    const param = new NullableRangeParameter(
      json.title,
      json.active,
      json.initial,
      json.min,
      json.max,
      json.integerStep
    );
    param.set(json.value);
    return param;
  }
}

export interface KeywordParameterJson extends ParamJson<string> {
  title: string;
}

export class KeywordParameter extends Parameter {
  static $_kind = 'keyword_parameter';
  public $_value: string = '';

  constructor(readonly $_title: string) {
    super();
  }

  set(keyword: string) {
    this.$_value = keyword;
  }

  serialize(): KeywordParameterJson {
    return {kind: KeywordParameter.$_kind, title: this.$_title, value: this.$_value};
  }

  static deserialize(json: KeywordParameterJson): KeywordParameter {
    const param = new KeywordParameter(json.title);
    param.set(json.value);
    return param;
  }
}
